import { ChangeEvent, FC, useRef } from 'react';
import { OutlineButton } from '@/ui/components/OutlineButton';

interface UploadFilesButtonProps {
  /** Called with the user-picked files (already MIME-filtered to PDFs). */
  onFilesPicked: (files: File[]) => void;
  /** When true the button is greyed out and clicks are no-ops. Gated on
   *  `caseStatus.canUploadDocuments` + access control. */
  disabled?: boolean;
}

/**
 * Header-bar "Upload files" button — opens the OS file picker, filters
 * the result to PDFs, and forwards them to `onFilesPicked`. Same visual
 * + behaviour as the MedicalRecordsPage `Upload files` button so both
 * upload entry points feel like the same control.
 */
export const UploadFilesButton: FC<UploadFilesButtonProps> = ({ onFilesPicked, disabled = false }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newFiles = Array.from(selectedFiles);
    const allowedTypes = ['application/pdf'];
    const allowedFiles = newFiles.filter((file) => allowedTypes.includes(file.type));
    if (allowedFiles.length === 0) return;

    onFilesPicked(allowedFiles);
    // Clear the input so picking the same file again still fires onChange.
    event.target.value = '';
  };

  return (
    <>
      <input ref={fileInputRef} type="file" hidden onChange={handleChange} accept=".pdf" multiple disabled={disabled} />
      <OutlineButton
        className="!h-8 shrink-0 !bg-white whitespace-nowrap [&_.pi]:text-[12px]"
        onClick={handleClick}
        disabled={disabled}
      >
        <i className="pi pi-arrow-up"></i>
        <span className="ml-2">Upload files</span>
      </OutlineButton>
    </>
  );
};
